import { lazy } from "react"

import { Agentation } from "agentation"
import { useLocation } from "react-router-dom"
import { RecoilRoot } from "recoil"

import App from "./App"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { FeedbackModal } from "./components/FeedbackModal"
import { BatteryOnlyProposalRoutes } from "./containers/BatteryOnlyProposal/BatteryOnlyProposalRoutes"
import { isBatteryProposalPath } from "./containers/BatteryOnlyProposal/isBatteryProposalPath"
import { BATTERY_PRINT_PRESENTATION_PATH } from "./containers/BatteryOnlyProposal/setup/setup-steps"
import { isSolarProposalPath } from "./containers/SolarProposal/isSolarProposalPath"
import { SOLAR_PRINT_PRESENTATION_PATH } from "./containers/SolarProposal/solar-setup-paths"
import { SolarProposalRoutes } from "./containers/SolarProposal/SolarProposalRoutes"
import GraphqlClientProvider from "./hooks/graphql"
import IS_DEV from "./utils/isDev"

const Logout = lazy(() => import(`./components/Logout`))

const PublicPresentation = lazy(
  () => import(`./containers/PublicPresentation/PublicPresentation`),
)

const PublicBatteryPresentation = lazy(
  () =>
    import(`./containers/PublicBatteryPresentation/PublicBatteryPresentation`),
)

const BatterySavingsReportPrint = lazy(
  () =>
    import(`./containers/BatteryOnlyProposal/print/BatterySavingsReportPrint`),
)

const BatteryPrintPresentation = lazy(
  () =>
    import(`./containers/BatteryOnlyProposal/print/BatteryPrintPresentation`),
)

const PublicSolarPresentation = lazy(
  () => import(`./containers/PublicSolarPresentation/PublicSolarPresentation`),
)

const SolarPrintPresentation = lazy(
  () => import(`./containers/PublicSolarPresentation/SolarPrintPresentation`),
)

const SolarAppTree = (): JSX.Element => (
  <GraphqlClientProvider>
    <RecoilRoot>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </RecoilRoot>
    {!IS_DEV && <FeedbackModal />}
  </GraphqlClientProvider>
)

export const RootSwitch = (): JSX.Element => {
  const { pathname, search } = useLocation()
  const printSolarMatch = /^\/print\/presentation\/([^/]+)\/?$/.exec(pathname)
  const publicMatch = /^\/public\/presentation\/([^/]+)\/?$/.exec(pathname)
  // A Battery link is its own tree: no Recoil, no GraphQL client, no Solar
  // brochure. The whole prefix belongs to it, so a truncated or malformed
  // link shows the unavailable-link page instead of the seller login.
  const publicBatteryMatch = /^\/public\/battery-presentation(?:\/(.*))?$/.exec(
    pathname,
  )
  // The brochure print route the gateway hands to Chromium. Same standalone
  // tree as the recipient's: the document must not depend on a seller session.
  const printBatteryMatch = new RegExp(
    `^${BATTERY_PRINT_PRESENTATION_PATH}(?:/(.*))?$`,
  ).exec(pathname)
  // Solar Proposal V2 links and their print route. V1 links keep their own
  // /public/presentation/ prefix and brochure; these never fall back to it.
  const publicSolarV2Match = /^\/public\/solar-presentation(?:\/(.*))?$/.exec(
    pathname,
  )
  const printSolarV2Match = new RegExp(
    `^${SOLAR_PRINT_PRESENTATION_PATH}(?:/(.*))?$`,
  ).exec(pathname)
  const savingsReportMatch = /^\/print\/battery-savings-report(?:\/(.*))?$/.exec(
    pathname,
  )
  // Logout only clears the session, so it must not wait on App's profile read:
  // a session that can't load its profile is the one most worth leaving.
  const logoutMatch = /^\/logout\/?$/.test(pathname)
  const tree = logoutMatch ? (
    <Logout />
  ) : savingsReportMatch ? (
    <BatterySavingsReportPrint
      token={savingsReportMatch[1]?.replace(/\/$/, ``) ?? ``}
    />
  ) : printSolarMatch ? (
    <PublicPresentation token={printSolarMatch[1]} print />
  ) : publicMatch ? (
    <PublicPresentation token={publicMatch[1]} />
  ) : publicBatteryMatch ? (
    <PublicBatteryPresentation
      token={publicBatteryMatch[1]?.replace(/\/$/, ``) ?? ``}
    />
  ) : printBatteryMatch ? (
    <BatteryPrintPresentation
      token={printBatteryMatch[1]?.replace(/\/$/, ``) ?? ``}
    />
  ) : publicSolarV2Match ? (
    <PublicSolarPresentation
      token={publicSolarV2Match[1]?.replace(/\/$/, ``) ?? ``}
    />
  ) : printSolarV2Match ? (
    <SolarPrintPresentation
      token={printSolarV2Match[1]?.replace(/\/$/, ``) ?? ``}
      search={search}
    />
  ) : isBatteryProposalPath(pathname) ? (
    <BatteryOnlyProposalRoutes />
  ) : isSolarProposalPath(pathname) ? (
    <SolarProposalRoutes />
  ) : (
    <SolarAppTree />
  )

  return (
    <>
      {tree}
      {/* The print route is rendered by Chromium to make a customer-facing
          document, and a fixed overlay lands on every page of it. Dev tooling
          stays off that route so a local brochure matches a released one. */}
      {import.meta.env.DEV &&
      savingsReportMatch == null &&
      printBatteryMatch == null &&
      printSolarMatch == null &&
      printSolarV2Match == null ? (
        <Agentation />
      ) : null}
    </>
  )
}
